import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import useWorkspace from '../hooks/useWorkspace'
import './AppLayout.css'
import { DOCUMENTS } from '../lib/documents'

const ABC_CORP_ORG_ID = 'd8d7e2a9-c14d-4500-a0fa-1405c011d21f'
const DOCUMENT_PINNED_SLUGS = ['staffing-overview', 'technical-roadmap']

const ICON_LIST = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
const ICON_WORKBENCH = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
const ICON_CALENDAR = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
const ICON_KANBAN = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="5" height="18" rx="1"/><rect x="9.5" y="3" width="5" height="12" rx="1"/><rect x="16" y="3" width="5" height="15" rx="1"/></svg>
const ICON_DASHBOARD = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
const ICON_FORM = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="12" y2="16"/></svg>

export default function AppLayout() {
    const navigate = useNavigate()
    const location = useLocation()
    const { user, profile, orgs, activeOrg, setActiveOrg, loading, isAdmin } = useWorkspace()
    const [searchTerm, setSearchTerm] = useState('')
    const [searchResults, setSearchResults] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [searchOpen, setSearchOpen] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [orgMenuOpen, setOrgMenuOpen] = useState(false)
    const [openSections, setOpenSections] = useState({
        workspace: false,
        clients: false,
        delivery: false,
        time: false,
        agile: false,
        staffing: false,
        insights: false,
        admin: false,
        documents: false,
    })
    const [openNavGroups, setOpenNavGroups] = useState({
        'documents:technical': false,
    })
    const searchRef = useRef(null)
    const menuRef = useRef(null)

    const navSections = useMemo(() => {
        const docsBySlug = DOCUMENTS.reduce((acc, doc) => {
            acc[doc.slug] = doc
            return acc
        }, {})
        const documentItems = [
            ...(docsBySlug['staffing-overview']
                ? [{ to: `/documents/${docsBySlug['staffing-overview'].slug}`, label: docsBySlug['staffing-overview'].label }]
                : []),
            ...(docsBySlug['technical-roadmap']
                ? [{ to: `/documents/${docsBySlug['technical-roadmap'].slug}`, label: docsBySlug['technical-roadmap'].label }]
                : []),
            {
                id: 'technical',
                label: 'Technical',
                items: DOCUMENTS
                    .filter(doc => !DOCUMENT_PINNED_SLUGS.includes(doc.slug))
                    .map(doc => ({ to: `/documents/${doc.slug}`, label: doc.label })),
            },
        ]

        return [
        {
            id: 'workspace',
            label: 'Workspace',
            items: [
                { to: '/dashboard', label: 'Overview' },
            ],
        },
        {
            id: 'clients',
            label: 'Client Operations',
            items: [
                { to: '/list-view?table=customers', label: 'Customers', icon: ICON_LIST },
                { to: '/list-view?table=invoices', label: 'Invoices', icon: ICON_LIST },
            ],
        },
        {
            id: 'delivery',
            label: 'Delivery',
            items: [
                { to: '/workbench', label: 'Workbench', icon: ICON_WORKBENCH },
                { to: '/list-view?table=projects', label: 'Projects', icon: ICON_LIST },
                { to: '/list-view?table=project-tasks', label: 'Project Tasks', icon: ICON_LIST },
            ],
        },
        {
            id: 'time',
            label: 'Time',
            items: [
                { to: '/time-keeper', label: 'Time Keeper', icon: ICON_CALENDAR },
                { to: '/list-view?table=time-cards', label: 'Time Cards', icon: ICON_LIST },
            ],
        },
        {
            id: 'agile',
            label: 'Agile Planning',
            items: [
                { to: '/boards', label: 'Boards', icon: ICON_KANBAN },
                { to: '/list-view?table=sprints', label: 'Sprints', icon: ICON_LIST },
                { to: '/list-view?table=stories', label: 'Stories', icon: ICON_LIST },
                { to: '/list-view?table=epics', label: 'Epics', icon: ICON_LIST },
                { to: '/under-construction?item=Backlog', label: 'Backlog' },
                { to: '/under-construction?item=Roadmap', label: 'Roadmap' },
            ],
        },
        {
            id: 'staffing',
            label: 'Staffing',
            items: [
                { to: '/staffing/overview', label: 'Staffing Dashboard', icon: ICON_DASHBOARD, linkedDashboard: 'd8b6794f-0aa4-4c91-bed1-b4c7ea5db09f' },
                { to: '/staffing/time-entry', label: 'Contractor Time Entry', icon: ICON_FORM },
                { to: '/list-view?table=staff-goals', label: 'Goals', icon: ICON_LIST },
                { to: '/list-view?table=staff-contracts', label: 'Staffing Contracts', icon: ICON_LIST },
                { to: '/list-view?table=staff-assigned', label: 'Staff Assigned', icon: ICON_LIST },
                { to: '/list-view?table=staff-time-cards', label: 'Time Cards', icon: ICON_LIST },
                { to: '/list-view?table=staff-placements', label: 'Direct Placements', icon: ICON_LIST },
                { to: '/list-view?table=staff-commissions', label: 'Commissions', icon: ICON_LIST },
            ],
        },
        {
            id: 'insights',
            label: 'Insights',
            items: [
                { to: '/reports', label: 'Reports', icon: ICON_LIST },
                { to: '/dashboards', label: 'Dashboards', icon: ICON_LIST },
                { to: '/under-construction?item=Forecasts', label: 'Forecasts' },
            ],
        },
        {
            id: 'admin',
            label: 'Admin',
            items: [
                { to: '/admin/org', label: 'Org Management' },
                { to: '/admin/table-config', label: 'Forms & Lists' },
                { to: '/admin/vibe-leaders', label: 'Vibe Leaders' },
                ...(activeOrg?.id && activeOrg.id !== ABC_CORP_ORG_ID
                    ? [{ to: '/admin/demo-data', label: 'Demo Data' }]
                    : []),
            ],
        },
        {
            id: 'documents',
            label: 'Documents',
            items: documentItems,
        },
        ]
    }, [activeOrg?.id])

    useEffect(() => {
        const handleClick = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false)
                setOrgMenuOpen(false)
            }
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setSearchOpen(false)
            }
        }
        window.addEventListener('mousedown', handleClick)
        return () => window.removeEventListener('mousedown', handleClick)
    }, [])

    useEffect(() => {
        if (!searchTerm.trim()) {
            setSearchResults([])
            return
        }
        const handler = setTimeout(() => {
            runTypeahead(searchTerm.trim())
        }, 250)
        return () => clearTimeout(handler)
    }, [searchTerm, activeOrg?.id])

    useEffect(() => {
        if (!loading && !user) {
            navigate('/signin')
        }
    }, [loading, user, navigate])

    async function runTypeahead(term) {
        if (!term || !activeOrg?.id) return
        setSearchLoading(true)
        const orgId = activeOrg?.id
        // Get project IDs for task search
        const { data: projIds } = await supabase.from('projects').select('id').eq('organization_id', orgId)
        const projectIds = (projIds || []).map(p => p.id)

        const [customersRes, projectsRes, tasksRes, invoicesRes] = await Promise.all([
            supabase.from('customers').select('id, name, company').eq('organization_id', orgId).ilike('name', `%${term}%`).limit(5),
            supabase.from('projects').select('id, name, status').eq('organization_id', orgId).ilike('name', `%${term}%`).limit(5),
            projectIds.length > 0
                ? supabase.from('project_tasks').select('id, title, status').in('project_id', projectIds).ilike('title', `%${term}%`).limit(5)
                : Promise.resolve({ data: [] }),
            supabase.from('invoices').select('id, invoice_number, status, organization_id').eq('organization_id', orgId).ilike('invoice_number', `%${term}%`).limit(5),
        ])

        const results = [
            ...(customersRes.data || []).map(item => ({ id: item.id, label: item.name, meta: item.company || 'Customer', type: 'Customer', to: `/list-view?table=customers&search=${encodeURIComponent(term)}` })),
            ...(projectsRes.data || []).map(item => ({ id: item.id, label: item.name, meta: item.status, type: 'Project', to: `/list-view?table=projects&search=${encodeURIComponent(term)}` })),
            ...(tasksRes.data || []).map(item => ({ id: item.id, label: item.title, meta: item.status, type: 'Task', to: `/list-view?table=project-tasks&search=${encodeURIComponent(term)}` })),
            ...(invoicesRes.data || []).map(item => ({ id: item.id, label: item.invoice_number, meta: item.status, type: 'Invoice', to: `/list-view?table=invoices&search=${encodeURIComponent(term)}` })),
        ]
        setSearchResults(results.slice(0, 5))
        setSearchLoading(false)
    }

    function handleSearchSubmit(event) {
        event.preventDefault()
        const term = searchTerm.trim()
        if (!term) return
        navigate(`/search?q=${encodeURIComponent(term)}`)
        setSearchOpen(false)
    }

    async function handleSignOut() {
        await supabase.auth.signOut()
        navigate('/')
    }

    if (loading) {
        return (
            <div className="app-layout-loading">
                <div className="loading-spinner"></div>
                <p>Loading your workspace...</p>
            </div>
        )
    }
    if (!user) {
        return null
    }

    const displayName = profile?.full_name || user?.email?.split('@')[0] || 'User'
    const displayEmail = profile?.email || user?.email || 'unknown@email.com'
    const avatarInitial = displayName.slice(0, 1).toUpperCase()
    const avatarUrl = profile?.avatar_url || `data:image/svg+xml;utf8,${encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64'><rect width='100%' height='100%' rx='32' fill='#1f2937'/><text x='50%' y='50%' dominant-baseline='central' text-anchor='middle' font-family='Inter, sans-serif' font-size='28' fill='#e2e8f0'>${avatarInitial}</text></svg>`
    )}`
    const orgName = activeOrg?.name || 'Organization'
    const orgInitial = orgName.slice(0, 1).toUpperCase()
    const orgLogoUrl = activeOrg?.logo_url || `data:image/svg+xml;utf8,${encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64'><rect width='100%' height='100%' rx='32' fill='#111827'/><text x='50%' y='50%' dominant-baseline='central' text-anchor='middle' font-family='Inter, sans-serif' font-size='28' fill='#93c5fd'>${orgInitial}</text></svg>`
    )}`

    const sectionVisibility = activeOrg?.nav_sections || {}
    const visibleSections = navSections.filter(section => {
        if (section.id === 'admin') return isAdmin
        return sectionVisibility[section.id] !== false
    })

    return (
        <div className="app-layout">
            <header className="app-header">
                <div className="app-header-left">
                    <span className="app-logo gradient-text">4ge</span>
                    <div className="app-header-divider"></div>
                    <div>
                        <div className="app-header-title">Forge Workspace</div>
                        <div className="app-header-subtitle">PSA hub</div>
                    </div>
                </div>
                <div className="app-header-center">
                    <form className="app-search" onSubmit={handleSearchSubmit} ref={searchRef}>
                        <span>Search</span>
                        <input
                            type="search"
                            placeholder="Search customers, projects, tasks..."
                            value={searchTerm}
                            onChange={(event) => {
                                setSearchTerm(event.target.value)
                                setSearchOpen(true)
                            }}
                            onFocus={() => setSearchOpen(true)}
                        />
                        {searchOpen && (
                            <div className="app-search-results">
                                {searchLoading && <div className="app-search-empty">Searching...</div>}
                                {!searchLoading && searchResults.length === 0 && (
                                    <div className="app-search-empty">No matches yet. Press enter to search.</div>
                                )}
                                {!searchLoading && searchResults.map(result => (
                                    <button
                                        key={result.id}
                                        className="app-search-result"
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => {
                                            navigate(result.to)
                                            setSearchOpen(false)
                                        }}
                                        type="button"
                                    >
                                        <div>
                                            <div className="app-search-result-title">{result.label}</div>
                                            <div className="app-search-result-meta">{result.meta}</div>
                                        </div>
                                        <span className="app-search-result-type">{result.type}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </form>
                </div>
                <div className="app-header-right" ref={menuRef}>
                    <div className="app-profile-shell">
                        <button
                            className="app-profile"
                            onClick={() => {
                                setMenuOpen(prev => !prev)
                                setOrgMenuOpen(false)
                            }}
                            type="button"
                        >
                            <img className="app-avatar" src={avatarUrl} alt={displayName} />
                            <span className="app-profile-text">
                                <span className="app-profile-name">{displayName}</span>
                                <span className="app-profile-email">{displayEmail}</span>
                            </span>
                        </button>
                        <button
                            className="app-org-button"
                            onClick={() => {
                                setOrgMenuOpen(prev => !prev)
                                setMenuOpen(false)
                            }}
                            type="button"
                            aria-label="Switch organization"
                        >
                            <img className="app-org-avatar" src={orgLogoUrl} alt={orgName} />
                        </button>
                    </div>
                    {menuOpen && (
                        <div className="app-profile-menu">
                            <button className="app-profile-menu-item" onClick={() => navigate('/profile')} type="button">
                                Profile settings
                            </button>
                            <button className="app-profile-menu-item danger" onClick={handleSignOut} type="button">
                                Sign out
                            </button>
                        </div>
                    )}
                    {orgMenuOpen && (
                        <div className="app-org-menu">
                            <div className="app-org-menu-title">Switch organization</div>
                            {orgs.map(org => (
                                <button
                                    key={org.id}
                                    className={`app-org-menu-item${org.id === activeOrg?.id ? ' is-active' : ''}`}
                                    onClick={() => {
                                        if (org.id !== activeOrg?.id) {
                                            setActiveOrg(org.id)
                                            setOrgMenuOpen(false)
                                            if (typeof window !== 'undefined') {
                                                window.location.reload()
                                            }
                                            return
                                        }
                                        setActiveOrg(org.id)
                                        setOrgMenuOpen(false)
                                    }}
                                    type="button"
                                >
                                    <span className="app-org-menu-name">{org.name}</span>
                                    {org.id === activeOrg?.id && <span className="app-org-menu-current">Current</span>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </header>

            <aside className="app-sidebar">
                {visibleSections.map(section => (
                    <div key={section.id} className="app-sidebar-section">
                        <button
                            className="app-sidebar-toggle"
                            onClick={() =>
                                setOpenSections(prev => ({
                                    ...prev,
                                    [section.id]: !prev[section.id],
                                }))
                            }
                            type="button"
                            aria-expanded={openSections[section.id]}
                        >
                            <span className="app-sidebar-label">{section.label}</span>
                            <span className="app-sidebar-chevron">{openSections[section.id] ? '▾' : '▸'}</span>
                        </button>
                        {openSections[section.id] && (
                            <nav className="app-nav">
                                {section.items.map(item => {
                                    if (item.items) {
                                        const navGroupKey = `${section.id}:${item.id || item.label}`
                                        const current = location.pathname + location.search
                                        const hasActiveChild = item.items.some(child => {
                                            if (child.to.includes('?')) return current === child.to
                                            return location.pathname === child.to || location.pathname.startsWith(child.to + '/')
                                        })
                                        const isGroupOpen = openNavGroups[navGroupKey] || hasActiveChild

                                        return (
                                            <div key={navGroupKey} className="app-nav-group">
                                                <button
                                                    className="app-nav-group-toggle"
                                                    onClick={() =>
                                                        setOpenNavGroups(prev => ({
                                                            ...prev,
                                                            [navGroupKey]: !prev[navGroupKey],
                                                        }))
                                                    }
                                                    type="button"
                                                    aria-expanded={isGroupOpen}
                                                >
                                                    <span className="app-nav-group-label">{item.label}</span>
                                                    <span className="app-nav-group-chevron">{isGroupOpen ? 'v' : '>'}</span>
                                                </button>
                                                {isGroupOpen && (
                                                    <div className="app-nav-group-items">
                                                        {item.items.map(child => {
                                                            const isActive = child.to.includes('?')
                                                                ? current === child.to
                                                                : location.pathname === child.to || location.pathname.startsWith(child.to + '/')
                                                            return (
                                                                <NavLink
                                                                    key={child.to}
                                                                    to={child.to}
                                                                    className={() => `app-nav-link app-nav-link-nested${isActive ? ' active' : ''}`}
                                                                >
                                                                    <span className="app-nav-dot"></span>
                                                                    {child.label}
                                                                </NavLink>
                                                            )
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    }

                                    const current = location.pathname + location.search
                                    const isActive = item.to.includes('?')
                                        ? current === item.to
                                        : location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                                    return (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            className={() => `app-nav-link${isActive ? ' active' : ''}`}
                                        >
                                            {item.icon
                                                ? <span className="app-nav-icon">{item.icon}</span>
                                                : <span className="app-nav-dot"></span>
                                            }
                                            {item.label}
                                        </NavLink>
                                    )
                                })}
                            </nav>
                        )}
                    </div>
                ))}
            </aside>

            <main className="app-main">
                <Outlet />
            </main>
        </div>
    )
}
